import { LINK_STATUSES, RAW_CONDITIONS } from "@pokifolio/contracts";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { Card, CardDescription, CardTitle } from "../../components/ui/card";
import { Select } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { RAW_CONDITION_LABELS } from "../research/format";
import { LINK_STATUS_LABELS } from "./card-format";
import { useCardStats } from "./card-queries";

const tile =
  "flex flex-col gap-1 rounded-lg border bg-card p-4 transition-colors hover:bg-accent/50 focus-visible:bg-accent/50";

/** Dashboard totals (D-66): cards, listings, listings by step and by condition; each opens the filtered list. */
export function CardStatsPanel() {
  const [scope, setScope] = useState<"all" | "mine">("all");
  const stats = useCardStats(scope);
  const mine = scope === "mine" ? { scope: "mine" as const } : {};
  const data = stats.data;

  return (
    <Card className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>Listings overview</CardTitle>
          <CardDescription>Click a number to see those listings.</CardDescription>
        </div>
        <Select
          aria-label="Whose listings"
          value={scope}
          onChange={(e) => setScope(e.target.value === "mine" ? "mine" : "all")}
          className="w-40"
        >
          <option value="all">Everyone</option>
          <option value="mine">Mine</option>
        </Select>
      </div>

      {data === undefined ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Link to="/cards" className={tile} aria-label={`${String(data.cards)} cards`}>
              <span className="text-2xl font-bold">{data.cards}</span>
              <span className="text-xs text-muted-foreground">Cards</span>
            </Link>
            <Link
              to="/listings"
              search={mine}
              className={tile}
              aria-label={`${String(data.listings)} listings`}
            >
              <span className="text-2xl font-bold">{data.listings}</span>
              <span className="text-xs text-muted-foreground">Listings</span>
            </Link>
            {LINK_STATUSES.map((status) => (
              <Link
                key={status}
                to="/listings"
                search={{ ...mine, status }}
                className={tile}
                aria-label={`${String(data.byStatus[status])} ${LINK_STATUS_LABELS[status]}`}
              >
                <span className="text-2xl font-bold">{data.byStatus[status]}</span>
                <span className="text-xs text-muted-foreground">{LINK_STATUS_LABELS[status]}</span>
              </Link>
            ))}
          </div>
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Working lists
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(
                [
                  [
                    "active",
                    "Active",
                    data.byDeal.open + data.byDeal.opened + data.byDeal.no_buy + data.byDeal.buying,
                  ],
                  ["negotiating", "In negotiation", data.byDeal.negotiating],
                  ["purchased", "Purchased", data.byDeal.purchased],
                  ["closed", "Closed", data.byDeal.closed],
                ] as const
              ).map(([view, label, n]) => (
                <Link
                  key={view}
                  to="/listings"
                  search={{ ...mine, ...(view === "active" ? {} : { view }) }}
                  className={tile}
                  aria-label={`${String(n)} ${label}`}
                >
                  <span className="text-xl font-semibold">{n}</span>
                  <span className="text-xs text-muted-foreground">{label}</span>
                </Link>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              By condition
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {RAW_CONDITIONS.map((condition) => (
                <Link
                  key={condition}
                  to="/listings"
                  search={{ ...mine, condition }}
                  className={tile}
                  aria-label={`${String(data.byCondition[condition])} ${RAW_CONDITION_LABELS[condition]}`}
                >
                  <span className="text-xl font-semibold">{data.byCondition[condition]}</span>
                  <span className="text-xs text-muted-foreground">{RAW_CONDITION_LABELS[condition]}</span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
